'use client'

import React from 'react'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { useProjectStore } from '@/hooks/use-project-store'
import { cn } from '@/lib/utils'
import { useTranslations } from 'next-intl'

type GradientTooltipProps = {
  content: React.ReactNode
  children: React.ReactNode
  side?: 'top' | 'bottom' | 'left' | 'right'
  align?: 'start' | 'center' | 'end'
  sideOffset?: number
  className?: string
  alwaysShow?: boolean
}

export const GradientTooltip = ({
  content,
  children,
  side = 'bottom',
  align = 'center',
  sideOffset,
  className,
  alwaysShow = false,
}: GradientTooltipProps) => {
  const [open, setOpen] = React.useState(alwaysShow)

  React.useEffect(() => {
    if (alwaysShow) {
      setOpen(true)
    }
  }, [alwaysShow])

  return (
    <Tooltip
      open={alwaysShow ? true : open}
      onOpenChange={alwaysShow ? undefined : setOpen}
    >
      <TooltipTrigger
        asChild
        onClick={() => !alwaysShow && setOpen(true)}
        onMouseEnter={() => !alwaysShow && setOpen(true)}
        onMouseLeave={() => !alwaysShow && setOpen(false)}
      >
        {children}
      </TooltipTrigger>
      <TooltipContent
        side={side}
        align={align}
        sideOffset={sideOffset}
        className={cn(
          'bg-popover text-popover-foreground border-0 rounded-[8px] py-2 px-3 max-w-56 z-[9999]',
          className,
        )}
      >
        {content}
      </TooltipContent>
    </Tooltip>
  )
}

type GradientTooltipForDisabledProps = {
  disabled?: boolean
  disabledContent?: string
  content: string
  children: React.ReactNode
  side?: 'top' | 'bottom' | 'left' | 'right'
  align?: 'start' | 'center' | 'end'
  className?: string
}

export const GradientTooltipForDisabled = ({
  disabled,
  disabledContent,
  content,
  children,
  side = 'bottom',
  align = 'center',
  className,
}: GradientTooltipForDisabledProps) => {
  const t = useTranslations('project-preview')

  const { aiStage } = useProjectStore()

  const isDisabled = disabled || aiStage === 'coding'

  return (
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent
        side={side}
        align={align}
        className={cn(
          'bg-popover text-popover-foreground border-0 backdrop-blur-[74px] rounded-[8px] py-2 px-3 max-w-56 z-[9999]',
          className,
        )}
      >
        {isDisabled
          ? disabledContent ||
            t('will_be_available_after_ai_finishes_developing')
          : content}
      </TooltipContent>
    </Tooltip>
  )
}
